import { EVENT_USER_LANGUAGE_CHANGED, EVENT_USER_PROFILE_CHANGED } from '@rio-cloud/rio-user-menu-component';
import { extractLanguage } from './lang/lang';
import { getLocale } from './lang/langSlice';
import { configureFetchDisplayMessages } from './lang/services';
import { userProfileObtained, userSessionExpired, userSessionRenewed } from './login/loginSlice';
import { configureMockUserManager, configureUserManager, createUserManager } from './login/login';
import { store } from './setup/store';
import { accessToken } from './tokenHandling/accessToken';
import { accessTokenStored, idTokenStored } from './tokenHandling/tokenHandlingSlice';
import { trace } from './setup/trace';
import { attemptInitialSignIn } from './setup/oauth';
import { config } from '../config';
import type { OAuthConfig, SessionRenewedResult } from './types';

export const main = (renderApp: () => void) => {
    const fetchDisplayMessages = configureFetchDisplayMessages(store);

    // We want the `<html lang>` attribute to be synced with the
    // language currently displayed
    store.subscribe(() => {
        const lang = extractLanguage(getLocale(store.getState())) || 'en-GB';
        const html = document.querySelector('html');

        if (html && html.getAttribute('lang') !== lang) {
            html.setAttribute('lang', lang);
        }
    });

    const oauthConfig: OAuthConfig = {
        onSessionExpired: () => {
            accessToken.discardAccessToken();
            store.dispatch(userSessionExpired());
        },
        onSessionRenewed: (result: SessionRenewedResult) => {
            trace('index.onTokenRenewed', result);
            accessToken.saveAccessToken(result.accessToken);
            store.dispatch(accessTokenStored(result.accessToken));
            store.dispatch(idTokenStored(result.idToken));
            store.dispatch(userProfileObtained(result.profile));

            store.dispatch(userSessionRenewed());

            // You will need to get the user language by yourself then
            // you may fetch the suitable messages from the CDN. Depending
            // on when and from where you fetch the user settings you might
            // want to employ a loading spinner while the request is ongoing.
            void fetchDisplayMessages(result.locale);
        },
    };

    const isAllowedToMockAuth = import.meta.env.DEV;
    // biome-ignore lint/complexity/useOptionalChain: suppress out of convenience during migration to biome
    const testAccessToken = sessionStorage && sessionStorage.getItem('test_access_token'); // enables mocking of authentication in production
    // biome-ignore lint/complexity/useOptionalChain: suppress out of convenience during migration to biome
    const testIdToken = sessionStorage && sessionStorage.getItem('test_id_token'); // enables mocking of authentication in production
    const userManager =
        (isAllowedToMockAuth && config.login.mockAuthorization) || (testAccessToken && testIdToken)
            ? configureMockUserManager(oauthConfig, { accessToken: testAccessToken, idToken: testIdToken })
            : configureUserManager(oauthConfig, createUserManager());

    document.addEventListener(EVENT_USER_LANGUAGE_CHANGED, userManager.signinSilent.bind(userManager) as EventListener);
    document.addEventListener(EVENT_USER_PROFILE_CHANGED, userManager.signinSilent.bind(userManager) as EventListener);

    attemptInitialSignIn(userManager)
        .then(renderApp)
        .catch(error => {
            trace('could not start application', error);
        });
};
